import { ETAWindow as ETAWindowType } from '../../types'
import { CrewCalculations } from '../../lib/crewCalculations'
import { Clock, Timer, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { Badge } from '../ui/badge'

interface ETAWindowProps {
  window: ETAWindowType
  startTime?: Date
  showElapsed?: boolean
  status?: 'on-pace' | 'ahead' | 'behind'
  speedFactor?: number
  className?: string
}

export function ETAWindow({ 
  window, 
  startTime, 
  showElapsed = false, 
  status = 'on-pace',
  speedFactor,
  className = '' 
}: ETAWindowProps) {
  const windowText = CrewCalculations.formatETAWindow(window, startTime, showElapsed)
  
  const getStatusInfo = () => {
    switch (status) {
      case 'ahead':
        return {
          icon: <TrendingUp className="h-3 w-3" />,
          color: 'bg-green-100 text-green-800 border-green-200',
          label: 'Ahead'
        }
      case 'behind':
        return {
          icon: <TrendingDown className="h-3 w-3" />,
          color: 'bg-red-100 text-red-800 border-red-200',
          label: 'Behind'
        }
      case 'on-pace':
      default:
        return {
          icon: <Minus className="h-3 w-3" />,
          color: 'bg-blue-100 text-blue-800 border-blue-200',
          label: 'On Pace'
        }
    }
  }

  const statusInfo = getStatusInfo()
  const confidence = Math.round(window.confidence * 100)

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Main ETA Window */}
      <div className="flex items-center space-x-2">
        {showElapsed || !startTime ? (
          <Timer className="h-4 w-4 text-gray-500" />
        ) : (
          <Clock className="h-4 w-4 text-gray-500" />
        )}
        <span className="font-mono text-lg font-semibold text-gray-900">
          {windowText}
        </span>
      </div>

      {/* Status and Confidence */}
      <div className="flex items-center justify-between">
        <Badge 
          variant="outline" 
          className={`flex items-center space-x-1 ${statusInfo.color}`}
        >
          {statusInfo.icon}
          <span className="text-xs font-medium">{statusInfo.label}</span>
        </Badge>
        
        <div className="flex items-center space-x-2 text-xs text-gray-500">
          {speedFactor && (
            <span className="font-medium">
              {speedFactor < 1 ? '⚡' : speedFactor > 1 ? '🐌' : '⚖️'} 
              {(speedFactor * 100).toFixed(0)}%
            </span>
          )}
          <span>
            {confidence}% confidence
          </span>
        </div>
      </div>
    </div>
  )
}

interface CompactETAWindowProps {
  window: ETAWindowType
  startTime?: Date
  showElapsed?: boolean
  status?: 'on-pace' | 'ahead' | 'behind'
  className?: string
}

// Compact version for mobile/print
export function CompactETAWindow({ 
  window, 
  startTime, 
  showElapsed = false, 
  status = 'on-pace',
  className = '' 
}: CompactETAWindowProps) {
  const windowText = CrewCalculations.formatETAWindow(window, startTime, showElapsed)
  
  const getStatusColor = () => {
    switch (status) {
      case 'ahead':
        return 'text-green-600'
      case 'behind':
        return 'text-red-600'
      case 'on-pace':
      default:
        return 'text-blue-600'
    }
  }

  const getStatusEmoji = () => {
    switch (status) {
      case 'ahead':
        return '🏃‍♂️💨'
      case 'behind':
        return '🐌'
      case 'on-pace':
      default:
        return '⏱️'
    }
  }

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <span className="text-lg">
        {getStatusEmoji()}
      </span>
      <span className={`font-mono text-base font-semibold ${getStatusColor()}`}>
        {windowText}
      </span>
    </div>
  )
}

interface ETACountdownProps {
  window: ETAWindowType
  startTime?: Date
  className?: string
}

// Countdown to earliest ETA
export function ETACountdown({ window, startTime, className = '' }: ETACountdownProps) {
  if (!startTime) {
    return null
  }

  const now = new Date()
  const earliestETA = new Date(startTime.getTime() + window.earliestMinutes * 60 * 1000)
  const timeToETA = earliestETA.getTime() - now.getTime()
  
  if (timeToETA <= 0) {
    return (
      <div className={`text-center ${className}`}>
        <div className="text-lg font-semibold text-green-600">
          Expected Now!
        </div>
        <div className="text-sm text-gray-500">
          Runner should be arriving
        </div>
      </div>
    )
  }

  const minutesToETA = Math.ceil(timeToETA / (1000 * 60))
  
  return (
    <div className={`text-center ${className}`}>
      <div className="text-2xl font-bold text-blue-600">
        {minutesToETA}m
      </div>
      <div className="text-sm text-gray-500">
        until earliest ETA
      </div>
    </div>
  )
}

interface ETAProgressProps {
  window: ETAWindowType
  actualTime?: number
  className?: string
}

// Progress bar showing where actual time falls in the window
export function ETAProgress({ window, actualTime, className = '' }: ETAProgressProps) {
  if (!actualTime) {
    return null
  }

  const windowSpan = window.latestMinutes - window.earliestMinutes
  let progress = 0
  let color = 'bg-blue-500'
  
  if (actualTime < window.earliestMinutes) {
    // Ahead of window
    progress = 0
    color = 'bg-green-500'
  } else if (actualTime > window.latestMinutes) {
    // Behind window
    progress = 100
    color = 'bg-red-500'
  } else {
    // Within window
    progress = ((actualTime - window.earliestMinutes) / windowSpan) * 100
    color = 'bg-blue-500'
  }

  return (
    <div className={`space-y-1 ${className}`}>
      <div className="flex justify-between text-xs text-gray-500">
        <span>Early</span>
        <span>On Time</span>
        <span>Late</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className={`h-2 rounded-full transition-all duration-300 ${color}`}
          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
        />
      </div>
      <div className="text-center text-xs text-gray-600">
        Actual: {CrewCalculations.formatElapsedTime(actualTime)}
      </div>
    </div>
  )
}